import { Edit3, Eye, MapPin, Trash2 } from "lucide-react";
import type { Shop } from "../types";
import { hasExactLocation } from "../utils";
import { displayValue } from "../../../shared/utils/format";

type ShopListProps = {
  shops: Shop[];
  isLoading: boolean;
  selectedShopId: string | null;
  onSelectShop: (shop: Shop) => void;
  onEditShop: (shop: Shop) => void;
  onDeleteShop: (shop: Shop) => void;
};

function LocationBadge({ shop }: { shop: Shop }) {
  if (hasExactLocation(shop.shopLocation)) {
    return <span className="status-badge exact">Has location</span>;
  }

  if (shop.shopLocation.status === "text_only") {
    return <span className="status-badge text-only">Text only</span>;
  }

  return <span className="status-badge missing">Missing</span>;
}

export function ShopList({
  shops,
  isLoading,
  selectedShopId,
  onSelectShop,
  onEditShop,
  onDeleteShop
}: ShopListProps) {
  if (isLoading) {
    return <div className="empty-state">Loading saved shops...</div>;
  }

  if (shops.length === 0) {
    return (
      <div className="empty-state">
        <MapPin size={24} />
        No shops match the current filters.
      </div>
    );
  }

  return (
    <section className="list-panel" aria-label="Shops list">
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Shop</th>
              <th>Class</th>
              <th>Governorate</th>
              <th>City</th>
              <th>District</th>
              <th>Location</th>
              <th aria-label="Actions"></th>
            </tr>
          </thead>
          <tbody>
            {shops.map((shop) => (
              <tr key={shop.id} className={shop.id === selectedShopId ? "selected-row" : ""}>
                <td>
                  <button type="button" className="table-shop-button" onClick={() => onSelectShop(shop)}>
                    <strong>{shop.shopName}</strong>
                    <span>{displayValue(shop.shopPhoneNumber, "No phone")}</span>
                  </button>
                </td>
                <td>{displayValue(shop.shopClass, "-")}</td>
                <td>{displayValue(shop.shopGovenorate, "-")}</td>
                <td>{displayValue(shop.shopCity, "-")}</td>
                <td>{displayValue(shop.shopDistrict, "-")}</td>
                <td>
                  <LocationBadge shop={shop} />
                </td>
                <td>
                  <div className="row-actions">
                    <button type="button" className="icon-button" title="View details" onClick={() => onSelectShop(shop)}>
                      <Eye size={16} />
                    </button>
                    <button type="button" className="icon-button" title="Edit shop" onClick={() => onEditShop(shop)}>
                      <Edit3 size={16} />
                    </button>
                    <button type="button" className="icon-button danger" title="Delete shop" onClick={() => onDeleteShop(shop)}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mobile-shop-list">
        {shops.map((shop) => (
          <article key={shop.id} className={`shop-card ${shop.id === selectedShopId ? "selected-card" : ""}`}>
            <button type="button" className="shop-card-main" onClick={() => onSelectShop(shop)}>
              <strong>{shop.shopName}</strong>
              <span>{displayValue(shop.shopPhoneNumber, "No phone")}</span>
            </button>
            <div className="shop-card-meta">
              <span>Class {displayValue(shop.shopClass, "-")}</span>
              <span>{displayValue(shop.shopGovenorate, "No governorate")}</span>
              <span>{displayValue(shop.shopDistrict, "No district")}</span>
            </div>
            <div className="shop-card-footer">
              <LocationBadge shop={shop} />
              <div className="row-actions">
                <button type="button" className="icon-button" title="Edit shop" onClick={() => onEditShop(shop)}>
                  <Edit3 size={16} />
                </button>
                <button type="button" className="icon-button danger" title="Delete shop" onClick={() => onDeleteShop(shop)}>
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}
